import { type ComponentProps, type ReactNode, useMemo } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';
import { Label } from './label';

function FieldSet({ className, ...props }: ComponentProps<'fieldset'>) {
    return (
        <fieldset
            data-slot="field-set"
            className={cn(
                'flex flex-col gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
                className,
            )}
            {...props}
        />
    );
}

function FieldLegend({
    className,
    variant = 'legend',
    ...props
}: ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
    return (
        <legend
            data-slot="field-legend"
            data-variant={variant}
            className={cn(
                'mb-3 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base',
                className,
            )}
            {...props}
        />
    );
}

function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="field-group"
            className={cn(
                'group/field-group @container/field-group flex w-full flex-col gap-5 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4',
                className,
            )}
            {...props}
        />
    );
}

const fieldVariants = cva('group/field flex w-full gap-3', {
    variants: {
        orientation: {
            vertical: 'flex-col',
            horizontal: 'flex-row items-center *:data-[slot=field-label]:flex-auto',
            responsive:
                'flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
        },
        alignControlToLabel: {
            true: '',
            false: '',
        },
        alignContentToTop: {
            true: '',
            false: '',
        },
    },
    compoundVariants: [
        {
            orientation: 'horizontal',
            alignControlToLabel: true,
            className: 'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-1',
        },
        {
            orientation: 'horizontal',
            alignContentToTop: true,
            className: 'has-[>[data-slot=field-content]]:items-start',
        },
    ],
    defaultVariants: {
        orientation: 'vertical',
        alignControlToLabel: true,
        alignContentToTop: true,
    },
});

type FieldOverrides = Omit<
    ComponentProps<'div'> & VariantProps<typeof fieldVariants>,
    'orientation'
> & {
    [key: `data-${string}`]: string | number | boolean | undefined;
};

function Field({
    className,
    orientation = 'vertical',
    alignControlToLabel,
    alignContentToTop,
    ...props
}: ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
    return (
        <div
            role="group"
            data-slot="field"
            data-orientation={orientation}
            className={cn(
                fieldVariants({ orientation, alignControlToLabel, alignContentToTop }),
                className,
            )}
            {...props}
        />
    );
}

function FieldContent({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="field-content"
            className={cn(
                'group/field-content flex flex-1 flex-col gap-0.5 leading-snug',
                className,
            )}
            {...props}
        />
    );
}

function FieldLabel({
    className,
    dimWhenDisabled,
    destructiveWhenInvalid = false,
    ...props
}: ComponentProps<typeof Label> & {
    dimWhenDisabled?: boolean;
    destructiveWhenInvalid?: boolean;
}) {
    return (
        <Label
            data-slot="field-label"
            className={cn(
                "group/field-label peer/field-label flex w-fit gap-2 leading-6 text-foreground has-[[data-state='checked']]:border-primary has-[[data-state='checked']]:bg-primary/10 has-[>[data-slot=field]]:rounded-sm has-[>[data-slot=field]]:border *:data-[slot=field]:p-3",
                'has-[>[data-slot=field][data-invalid=true]]:border-destructive',
                'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col',
                'has-[:focus-visible]:border-primary has-[:focus-visible]:[box-shadow:0_0_0_2px_var(--accent)]',
                dimWhenDisabled && 'group-data-[disabled=true]/field:opacity-70',
                destructiveWhenInvalid && 'group-data-[invalid=true]/field:text-destructive',
                className,
            )}
            {...props}
        />
    );
}

function FieldTitle({
    className,
    destructiveWhenInvalid = false,
    ...props
}: ComponentProps<'div'> & { destructiveWhenInvalid?: boolean }) {
    return (
        <div
            data-slot="field-label"
            className={cn(
                'flex w-fit items-center gap-2 text-base leading-6 font-normal text-foreground group-data-[disabled=true]/field:opacity-50',
                destructiveWhenInvalid && 'group-data-[invalid=true]/field:text-destructive',
                className,
            )}
            {...props}
        />
    );
}

function FieldDescription({
    className,
    destructiveWhenInvalid = true,
    ...props
}: ComponentProps<'p'> & { destructiveWhenInvalid?: boolean }) {
    return (
        <p
            data-slot="field-description"
            className={cn(
                'text-left text-sm font-normal text-muted-foreground group-has-data-horizontal/field:text-balance [[data-variant=legend]+&]:-mt-1.5',
                destructiveWhenInvalid && 'group-data-[invalid=true]/field:text-destructive',
                'last:mt-0 nth-last-2:-mt-1',
                '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
                className,
            )}
            {...props}
        />
    );
}

function FieldError({
    className,
    children,
    errors,
    ...props
}: ComponentProps<'div'> & {
    errors?: Array<{ message?: string } | undefined>;
}) {
    const content = useMemo(() => {
        if (children) {
            return children;
        }

        if (!errors?.length) {
            return null;
        }

        const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];

        if (uniqueErrors?.length === 1) {
            return uniqueErrors[0]?.message;
        }

        return (
            <ul className="ml-4 flex list-disc flex-col gap-1">
                {uniqueErrors.map(
                    (error) => error?.message && <li key={error.message}>{error.message}</li>,
                )}
            </ul>
        );
    }, [children, errors]);

    if (!content) {
        return null;
    }

    return (
        <div
            role="alert"
            data-slot="field-error"
            className={cn('text-sm font-normal text-destructive', className)}
            {...props}
        >
            {content}
        </div>
    );
}

function FieldSlot({
    description,
    descriptionId,
    errorMessage,
    errorId,
}: {
    description?: string;
    descriptionId?: string;
    errorMessage?: ReactNode | string;
    errorId?: string;
}) {
    if (errorMessage) return <FieldError id={errorId}>{errorMessage}</FieldError>;
    if (description) return <FieldDescription id={descriptionId}>{description}</FieldDescription>;
    return null;
}

export type { FieldOverrides };
export {
    Field,
    FieldContent,
    FieldDescription,
    FieldError,
    FieldGroup,
    FieldLabel,
    FieldLegend,
    FieldSet,
    FieldSlot,
    FieldTitle,
};
